import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { 
  Search, MessageSquare, Lightbulb, CheckCircle, Users, Star, 
  ArrowRight, Shield, Clock, Zap, Bot, UserCheck, Sparkles 
} from "lucide-react";
import Layout from "../components/layout/Layout";

export default function HowItWorksPage() {
  const steps = [
    {
      number: "01",
      icon: Bot,
      title: "Обстучите идею с AI-напарником",
      description: "Опишите задачу, и AI-партнёр поможет структурировать мысли, задаст правильные вопросы и подсветит слепые зоны — в любое время дня и ночи.",
      details: [
        "Доступен 24/7, без записи и ожидания",
        "Структурирует ваши мысли и задачу",
        "Подсвечивает слепые зоны и риски"
      ]
    },
    {
      number: "02",
      icon: Search,
      title: "Найдите живого эксперта",
      description: "Когда нужна глубина — подключайте практика из реального бизнеса. Выберите эксперта по специализации, опыту и отзывам.",
      details: [
        "Фильтры по специализации и опыту",
        "Реальные отзывы от других клиентов",
        "Гибкие форматы: звонок, видео, личная встреча"
      ]
    },
    {
      number: "03",
      icon: Sparkles,
      title: "Получите результат",
      description: "AI фиксирует ключевые выводы, а эксперт даёт конкретные рекомендации. Вы уходите с чётким планом действий, а не с абстрактными советами.",
      details: [
        "Конкретный план действий по вашей ситуации",
        "AI-саммари ключевых выводов и шагов",
        "Примеры из реальной практики эксперта"
      ]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "AI + живой эксперт",
      description: "Начните с AI-напарника для быстрого анализа, а для глубины подключите проверенного эксперта из реального бизнеса."
    },
    {
      icon: Clock,
      title: "Доступность 24/7",
      description: "AI-напарник всегда на связи. Обстучите идею в любое время, а эксперта подключите, когда будете готовы."
    },
    {
      icon: Zap,
      title: "Конкретный результат",
      description: "Не абстрактные советы, а чёткий план действий. AI фиксирует выводы, эксперт даёт рекомендации из практики."
    },
    {
      icon: Users,
      title: "Любая бизнес-задача",
      description: "Маркетинг, продукт, HR, стратегия, инвестиции — AI-напарник и эксперты для любой области."
    }
  ];

  const useCases = [
    {
      title: "Проверка бизнес-идеи",
      description: "Обстучите идею с AI, выявите слабые места, затем валидируйте с экспертом из индустрии"
    },
    {
      title: "Запуск нового продукта",
      description: "AI поможет структурировать go-to-market, эксперт подскажет подводные камни из своего опыта"
    },
    {
      title: "Решение сложных задач",
      description: "Разберите ситуацию с AI-напарником, а затем получите советы от практика, который проходил то же самое"
    },
    {
      title: "Карьерные консультации",
      description: "AI поможет сформулировать цели и вопросы, а эксперт поделится реальным опытом из нужной сферы"
    },
    {
      title: "Построение команды",
      description: "Обсудите с AI структуру и процессы, а HR-эксперт даст конкретные рекомендации по найму и мотивации"
    },
    {
      title: "Маркетинговая стратегия",
      description: "AI-напарник поможет собрать гипотезы, а маркетолог-практик проверит их на реалистичность"
    }
  ];

  return (
    <Layout>
      <div id="how-it-works_page">
        <section className="py-16 lg:py-24 bg-gradient-to-b from-primary-950/30 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-3xl mx-auto"
            >
              <h1 className="text-4xl lg:text-5xl font-medium text-white mb-6">
                Как это работает
              </h1>
              <p className="text-xl font-extralight text-neutral-300 leading-relaxed">
                AI-напарник помогает разобраться, живой эксперт — углубиться. 
                Три шага от идеи до плана действий
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16 lg:space-y-24">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col lg:flex-row gap-8 lg:gap-16 items-center ${
                    index % 2 === 1 ? "lg:flex-row-reverse" : ""
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-5xl lg:text-6xl font-extralight text-primary-500/30">
                        {step.number}
                      </span>
                      <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-primary-400" />
                      </div>
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-medium text-white mb-4">
                      {step.title}
                    </h2>
                    <p className="text-lg font-extralight text-neutral-300 mb-6 leading-relaxed">
                      {step.description}
                    </p>
                    <ul className="space-y-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0" />
                          <span className="text-lg font-extralight text-neutral-400">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-1 w-full">
                    <div className="aspect-video rounded-2xl bg-gradient-to-br from-primary-500/10 to-accent-500/10 border border-neutral-800 flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-primary-500/30" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24 bg-neutral-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-medium text-white mb-4">
                Почему выбирают нас
              </h2>
              <p className="text-xl font-extralight text-neutral-400">
                Преимущества платформы «Обстучим»
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-2xl bg-neutral-800/30 border border-neutral-800"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-lg font-extralight text-neutral-400">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-medium text-white mb-4">
                Для чего это подходит
              </h2>
              <p className="text-xl font-extralight text-neutral-400">
                Задачи, которые решают с AI-напарником и экспертами
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 rounded-2xl border border-neutral-800 hover:border-primary-500/30 transition-colors group"
                >
                  <h3 className="text-xl font-medium text-white mb-2 group-hover:text-primary-400 transition-colors">
                    {useCase.title}
                  </h3>
                  <p className="text-lg font-extralight text-neutral-400">
                    {useCase.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24 bg-gradient-to-br from-primary-950/50 via-neutral-950 to-accent-950/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl lg:text-4xl font-medium text-white mb-6">
                Готовы попробовать?
              </h2>
              <p className="text-xl font-extralight text-neutral-300 mb-8 max-w-2xl mx-auto">
                Начните с AI-напарника — он доступен прямо сейчас. А когда понадобится глубина, подключите эксперта из реального бизнеса
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/experts"
                  className="px-8 py-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-lg font-medium transition-colors flex items-center gap-2"
                >
                  Попробовать AI-напарника
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/register"
                  className="px-8 py-4 rounded-xl border border-neutral-700 hover:border-neutral-500 text-white text-lg font-extralight transition-colors"
                >
                  Найти эксперта
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
